import React, { useEffect, useState, useRef } from "react";
import { Col, Container, Row, Table, Form, Button, ButtonGroup } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import "jspdf-autotable";
import Select from "react-select";
import SparkApi from "../../api/SparkApi";
import jsPDF from "jspdf";
import "./transaction.css";
import { Link } from "react-router-dom";
import InfoPill from "../InfoPill";
import BarcodeScannerComponent from "react-qr-barcode-scanner";

const AvailableInventoryReports = () => {
    const [filteredInventory, setFilteredInventory] = useState([]);
    const [searchText, setSearchText] = useState();
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [rackOptions, setRackOptions] = useState([]);
    const [factoryData, setFactoryData] = useState([]);
    const [selectedIds, setSelectedIds] = useState({
        productId: [],
        rackId: [],
        warehouseId: [],
        factoryId: [],
        categoryId : [],
    });
    const [searchedPro, setSearchedPro] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const indexOfLastRecord = currentPage * pageSize;
    const indexOfFirstRecord = indexOfLastRecord - pageSize;
    const currentRecords = filteredInventory?.records?.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredInventory?.records?.length / pageSize);
    const [isScannerVisible, setScannerVisible] = useState(false); // Control the scanner visibility
    const [resetTriggered, setResetTriggered] = useState(false);
    const [categories , setCategories] = useState([]);
    
    /*---------  Start barcode ----------------*/
    const [barcodeRead, setBarcodeRead] = useState();
    const barcodeRef = useRef({
        timing: 0,
        data: "",
    });

    useEffect(() => {
        const barcodeReaded = () => {
            if (barcodeRef.current.data.length > 1 && (barcodeRef.current.data.includes("PRO") || barcodeRef.current.data.includes("RAC"))) {
                setBarcodeRead(barcodeRef.current.data.split("E")[0]);
            }
            barcodeRef.current = { timing: 0, data: "" };
        };

        let timeout;

        const handleKeypress = (e) => {
            const currentTime = e.timeStamp;
            const isWithinInterval =
                currentTime - barcodeRef.current.timing < 200;

            if (barcodeRef.current.data.length === 0 || isWithinInterval) {
                clearTimeout(timeout);
                barcodeRef.current.data += e.key;
                barcodeRef.current.timing = currentTime;
                timeout = setTimeout(barcodeReaded, 300);
            } else {
                clearTimeout(timeout);
                barcodeRef.current = {
                    timing: currentTime,
                    data: e.key,
                };
            }
        };

        window.addEventListener("keypress", handleKeypress);

        return () => {
            window.removeEventListener("keypress", handleKeypress);
            clearTimeout(timeout);
        };
    }, []);

    useEffect(() => {
        if (barcodeRead) {
            fetchProductDetails();
        }
    }, [barcodeRead]);

    const fetchProductDetails = async () => {
        try {
            
            const productResult = await SparkApi.fetchScannedProductDetails(barcodeRead);
    
            if (productResult.success) {
                setProductOptions([]);
                const selectedValues = productResult?.records?.map((result) => result?.result?.product_id) || [];
                
                setSelectedIds((prev) => ({
                    ...prev,
                    productId: [...prev.productId, ...selectedValues],
                }));

                setSearchedPro((prev) => {
                    const newProduct = {
                        label: productResult?.records[0]?.result?.product_name,
                        value: productResult?.records[0]?.result?.product_id,
                    };
                    const updatedProducts = prev.filter((product) => product.value !== newProduct.value);
                    return [...updatedProducts, newProduct];
                });  
                setResetTriggered(true); 
            } else {
                console.log("No data found or success is false.");
            }
        } catch (error) {
            console.error("Error fetching product details:", error);
        }
    };    
    //------------ End barcode ----------------

    useEffect(() => {
        fetchWarehouses();
        fetchProducts();
        fetchRacks();
        fetchFactory();
        fetchActiveCategories();
        applyFilters();
    }, []);

    const fetchFactory = async () => {
        try {
            const result = await SparkApi.fetchFactory();
            if (result && Array.isArray(result)) {
                setFactoryData(
                    result.map((sub) => ({ value: sub.id, label: sub.name }))
                );
            } else if (result) {
                setFactoryData([{ value: result.id, label: result.name }]);
            }
        } catch (err) {
            console.error("Error fetching factory:", err);
        }
    };

    const fetchActiveCategories = async () => {
        try {
            const result = await SparkApi.getProductCategory();
            if (result.success) {
                if (result && Array.isArray(result?.records)) {
                    setCategories(
                        result?.records?.filter((obj) => obj?.status === 'active').map((sub) => ({
                            value: sub.id,
                            label: sub.category,
                            status: sub.status,
                        }))
                    );
                }
            }
        } catch (error) {
            console.error("Error fetch categories :", error);
        }
    }

    const fetchWarehouses = async () => {
        try {
            const warehouses = await SparkApi.fetchWarehouse();
            setWarehouseOptions(
                warehouses.map((sub) => ({
                    value: sub.id,
                    label: sub.name,
                    factory_id: sub.factory_id,
                }))
            );
        } catch (error) {
            console.error("Error fetching warehouses:", error);
        }
    };

    const fetchProducts = async () => {
        try {
            if (searchText?.trim()) {
                const products = await SparkApi.getProductsBySearch(searchText);
                if (products.success) {
                    setProductOptions(
                        products.records.map((ProRec) => ({
                            value: ProRec.id,
                            label: ProRec.name,
                        }))
                    );
                } else {
                    setProductOptions([]);
                }
            } else {
                setProductOptions([]);
            }
        } catch (error) {
            setProductOptions([]);
        }
    };

    const fetchRacks = async () => {
        try {
            const racks = await SparkApi.fetchRacks();
            setRackOptions(
                racks.data.map((rackRec) => ({
                    value: rackRec.id,
                    label: rackRec.name,
                    warehouse_id: rackRec.warehouse_id,
                }))
            );
        } catch (error) {
            console.error("Error fetching racks:", error);
        }
    };

    const applyFilters = async () => {
        const appliedFilters = { ...selectedIds };
        try {
            const report = await SparkApi.reportProductFilter(appliedFilters);

            if (report) {
                setCurrentPage(1);
                setFilteredInventory(report);
            }
        } catch (error) {
            console.error("Error fetching report:", error);
        }
    };

    const resetFilters = async () => {
        setSelectedIds({
            productId: [],
            rackId: [],
            warehouseId: [],
            factoryId: [],
            categoryId: [],
        });
        setSearchedPro([]);
        setResetTriggered(true);
    };

    useEffect(() => {
        if (resetTriggered) {
            applyFilters();  
            setResetTriggered(false);  
        }
    }, [resetTriggered]);

    const downloadPDF = () => {
        const doc = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });
    
        doc.setFontSize(12);
        doc.text("Inventory Reports", 14, 10);

        const tableData = Object.entries(
            filteredInventory?.records?.reduce((acc, data) => {
                const { product_id, product_name, factory_name, warehouse_name, rack_name, active_quantity } = data;
        
                if (!acc[product_id]) acc[product_id] = { product_name, factories: {} };
                if (!acc[product_id].factories[factory_name]) acc[product_id].factories[factory_name] = {};
                if (!acc[product_id].factories[factory_name][warehouse_name]) acc[product_id].factories[factory_name][warehouse_name] = {};
        
                acc[product_id].factories[factory_name][warehouse_name][rack_name] =
                    (acc[product_id].factories[factory_name][warehouse_name][rack_name] || 0) + active_quantity;
        
                return acc;
            }, {})
        ).flatMap(([product_id, productData]) => {
            const { product_name, factories } = productData;
        
            let productTotal = 0;
        
            const rows = Object.entries(factories).flatMap(([factory, warehouses]) => {
                const factoryTotal = Object.entries(warehouses).reduce(
                    (sum, [warehouse, racks]) =>
                        sum + Object.values(racks).reduce((rackSum, qty) => rackSum + qty, 0),
                    0
                );
        
                let warehouseRows = [];
        
                Object.entries(warehouses).forEach(([warehouse, racks]) => {
                    const warehouseTotal = Object.values(racks).reduce(
                        (sum, qty) => sum + qty,
                        0
                    );
        
                    const rackRows = Object.entries(racks).map(([rack, quantity]) => {
                        // Accumulate to productTotal here
                        productTotal += quantity; 
                        return [
                            product_name,
                            factory,
                            warehouse,
                            rack,
                            quantity,
                        ];
                    });
        
                    rackRows.push([
                        { content: `Warehouse Total: ${warehouse}`, colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
                        { content: warehouseTotal, styles: { fontStyle: 'bold' } },
                    ]);
        
                    warehouseRows = warehouseRows.concat(rackRows);
                });
        
                warehouseRows.push([
                    { content: `Factory Total: ${factory}`, colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
                    { content: factoryTotal, styles: { fontStyle: 'bold' } },
                ]);
        
                return warehouseRows;
            });
        
            rows.push([
                { content: `Product Total: ${product_name}`, colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
                { content: productTotal, styles: { fontStyle: 'bold' } },
            ]);
        
            return rows;
        });
        
        doc.autoTable({
            head: [
                [
                "Product Name",
                "Factory Name",
                "Warehouse Name",
                "Rack Name",
                "Qty",
                ],
            ],
            body: tableData,
            didDrawCell: (data) => {
                doc.setLineDash([1, 1], 0);
            },
            styles: {
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
            },
            columnStyles: { 4: { halign: 'right' }},
            margin: { top: 20 },
        });
    
        doc.save("Inventory_Report.pdf");
    };

    const handleFirstPage = () => setCurrentPage(1);
    const handlePrevPage = () => setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
    const handleNextPage = () => setCurrentPage(prevPage => Math.min(prevPage + 1, totalPages));
    const handleLastPage = () => setCurrentPage(totalPages);
    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const handleScanButton = () => {
        setScannerVisible(true);
    };

    return (
        <Container>
            <Row className="my-3">
                    <Col lg={12}>
                        <div style={{ textAlign: "center" }}>
                            {isScannerVisible && (
                                <BarcodeScannerComponent
                                    width={250}
                                    height={250}
                                    facingMode="environment"
                                    onUpdate={(err, result) => {
                                    if (result){
                                        setBarcodeRead(result?.text);
                                        setScannerVisible(false);
                                    } 
                                    else {
                                        setBarcodeRead(null)
                                        // setScannerVisible(false);
                                    }
                                    }}
                                />
                            )}
                        </div>
                        <Card>
                            <Card.Header className="text-center">Product Inventory Report</Card.Header>
                            <Card.Body>
                                <Row className="mb-1 filter-body">
                                    <Col lg={3} sm={6}>
                                        <Select
                                            isMulti
                                            id="product_id"
                                            name="product_id"
                                            options={productOptions || searchedPro}
                                            value={searchedPro?.filter((option) => selectedIds.productId.includes(option.value))}
                                            onInputChange={(inputValue) => {
                                                setSearchText(inputValue);
                                                fetchProducts();
                                            }}

                                            onChange={(selectedOptions) => {
                                                const selectedValues = selectedOptions ? selectedOptions.map((option) => option.value) : [];
                                        
                                                setSelectedIds((prev) => ({
                                                    ...prev,
                                                    productId: selectedValues, 
                                                }));
                                        
                                                setSearchedPro((prev) => {
                                                    const updatedProducts = selectedOptions
                                                        ? selectedOptions.map((option) => ({
                                                            label: option.label,
                                                            value: option.value,
                                                        }))
                                                        : [];
                                        
                                                    const uniqueProducts = [
                                                        ...new Map([
                                                            ...prev,
                                                            ...updatedProducts,
                                                        ].map((item) => [item.value, item])).values(),
                                                    ];
                                        
                                                    return uniqueProducts;
                                                });
                                            }}
                                            isClearable={true}
                                            isSearchable={true}
                                            placeholder="--Enter Product Name--"
                                            required
                                        />
                                    </Col>

                                    <Col lg={3}  sm={6}>
                                        <Select
                                            isMulti
                                            name="factory"
                                            options={factoryData}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select Factory"
                                            value={factoryData?.filter((option) => selectedIds?.factoryId?.includes(option.value))}
                                            onChange={(selectedOptions) => {
                                                const selectedValues = selectedOptions ? selectedOptions?.map((option) => option.value) : [];
                                                setSelectedIds((prev) => ({
                                                    ...prev,
                                                    factoryId: selectedValues,
                                                }));
                                            }}
                                        />
                                    </Col>

                                    <Col lg={3} sm={6}>
                                        <Select
                                            isMulti
                                            name="warehouse"
                                            options={warehouseOptions}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select Warehouse"
                                            value={warehouseOptions?.filter((option) =>
                                                selectedIds?.warehouseId?.includes(option.value)
                                            )}
                                            onChange={(selectedOptions) => {
                                                const selectedValues = selectedOptions
                                                    ? selectedOptions?.map((option) => option.value)
                                                    : [];
                                                setSelectedIds((prev) => ({
                                                    ...prev,
                                                    warehouseId: selectedValues,
                                                }));
                                            }}
                                        />
                                    </Col>

                                    <Col lg={3} sm={6}>
                                        <Select
                                            isMulti
                                            name="rack"
                                            options={rackOptions}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select Rack"
                                            value={rackOptions?.filter((option) =>
                                                selectedIds?.rackId?.includes(option.value)
                                            )}
                                            onChange={(selectedOptions) => {
                                                const selectedValues = selectedOptions ? selectedOptions?.map((option) => option.value) : [];
                                                setSelectedIds((prev) => ({
                                                    ...prev,
                                                    rackId: selectedValues,
                                                }));
                                            }}
                                        />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col lg={3} sm={6}>
                                        <Select
                                            isMulti
                                            name="category"
                                            options={categories}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select Category"
                                            value={categories?.filter((option) => selectedIds?.categoryId?.includes(option.value))}
                                            onChange={(selectedOptions) => {
                                                const selectedValues = selectedOptions ? selectedOptions?.map((option) => option.value) : [];
                                                setSelectedIds((prev) => ({
                                                    ...prev,
                                                    categoryId: selectedValues,
                                                }));
                                            }}
                                        />
                                    </Col>
                                    <Col lg={4}>
                                        <div className="d-flex mt-1">
                                            <Button className="btn-sm" variant="success" onClick={applyFilters}>Search</Button>
                                            <Button className="btn-sm mx-2" variant="danger" onClick={resetFilters}>Reset</Button>
                                            <Button disabled={filteredInventory?.records?.length > 0 ? false : true} className="btn btn-sm btn-primary float-end" onClick={downloadPDF}>Download</Button>
                                            <Button className="btn-sm scanButton" onClick={handleScanButton}>Scan Barcode</Button>
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col lg={12}>
                                        <Row>
                                            <Col lg={4} sm={4} md={3} className="d-flex flex-col justify-content-end align-items-end">
                                            </Col>
                                            <Col lg={1} xs={4} sm={4} md={2} className="d-flex flex-col justify-content-start align-items-start mt-3 inventory-page-size">
                                                <Form.Select 
                                                    name="page_size" 
                                                    value={pageSize}
                                                    onChange={(e) => {
                                                        setCurrentPage(1);
                                                        setPageSize(parseInt(e.target.value)); 
                                                    }}
                                                >
                                                    <option value="5">5</option>
                                                    <option value="10">10</option>
                                                    <option value="15">15</option>
                                                    <option value="20">20</option>
                                                </Form.Select>
                                            </Col>
                                            <Col lg={4} xs={7} md={5} className="d-flex flex-col justify-content-start align-items-start">
                                                <div className="mb-3">
                                                    <InfoPill
                                                    left="Total Inventory"
                                                    right={filteredInventory ? filteredInventory?.records?.length : 0}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                        <Table id="inventory-table" className="inventory-report" responsive>
                                            <thead>
                                                <tr>
                                                    <th>Product Name</th>
                                                    <th>Factory Name</th>
                                                    <th>Warehouse Name</th>
                                                    <th>Racks Name</th>
                                                    <th>Qty</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredInventory?.records && Array.isArray(filteredInventory.records) ? (
                                                    Object.entries(
                                                        currentRecords?.reduce((acc, data) => {
                                                            const { product_id, product_name, factory_name, warehouse_name, rack_name, active_quantity } = data;

                                                            if (!acc[product_id]) acc[product_id] = { product_name, factories: {} };
                                                            if (!acc[product_id].factories[factory_name]) acc[product_id].factories[factory_name] = {};
                                                            if (!acc[product_id].factories[factory_name][warehouse_name]) acc[product_id].factories[factory_name][warehouse_name] = {};

                                                            acc[product_id].factories[factory_name][warehouse_name][rack_name] =
                                                                (acc[product_id].factories[factory_name][warehouse_name][rack_name] || 0) + active_quantity;

                                                            return acc;
                                                        }, {})
                                                    ).map(([product_id, productData]) => {
                                                        const { product_name, factories } = productData;

                                                        return (
                                                            <>
                                                                {Object.entries(factories).map(([factory, warehouses]) => (
                                                                    <>
                                                                        {Object.entries(warehouses).map(([warehouse, racks]) => (
                                                                            <>
                                                                                {Object.entries(racks).map(([rack, quantity]) => (
                                                                                    <tr key={`${product_id}-${factory}-${warehouse}-${rack}`}>
                                                                                        <td><Link to={`/productview/${product_id}`}>{product_name}</Link></td>
                                                                                        <td>{factory}</td>
                                                                                        <td>{warehouse}</td>
                                                                                        <td>{rack}</td>
                                                                                        <td>{quantity}</td>
                                                                                    </tr>
                                                                                ))}
                                                                                {/* Warehouse Total Row */}
                                                                                <tr key={`${product_id}-${factory}-${warehouse}-total`}>
                                                                                    <td style={{ fontWeight: "bold", textAlign: "right" }} colSpan="4">
                                                                                        <strong>Warehouse Total:</strong> {warehouse}
                                                                                    </td>
                                                                                    <td style={{ fontWeight: "bold", textAlign: "right" }}>
                                                                                        {Object.values(racks).reduce((sum, qty) => sum + qty, 0)}
                                                                                    </td>
                                                                                </tr>
                                                                            </>
                                                                        ))}
                                                                        {/* Factory Total Row */}
                                                                        <tr key={`${product_id}-${factory}-total`}>
                                                                            <td style={{ fontWeight: "bold", textAlign: "right" }} colSpan="4">
                                                                                <strong>Factory Total:</strong> {factory}
                                                                            </td>
                                                                            <td style={{ fontWeight: "bold", textAlign: "right" }}>
                                                                                {Object.values(warehouses).reduce(
                                                                                    (sum, racks) =>
                                                                                        sum +
                                                                                        Object.values(racks).reduce((rSum, qty) => rSum + qty, 0),
                                                                                    0
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    </>
                                                                ))}
                                                                {/* Product Total Row */}
                                                                <tr key={`${product_id}-total`}>
                                                                    <td style={{ fontWeight: "bold", textAlign: "right", backgroundColor: "#f0f0f0" }} colSpan="4">
                                                                        <strong>Product Total:</strong> {product_name}
                                                                    </td>
                                                                    <td style={{ fontWeight: "bold", textAlign: "right", backgroundColor: "#f0f0f0" }}>
                                                                        {Object.values(factories).reduce(
                                                                            (productTotal, warehouses) =>
                                                                                productTotal +
                                                                                Object.values(warehouses).reduce(
                                                                                    (factoryTotal, racks) =>
                                                                                        factoryTotal +
                                                                                        Object.values(racks).reduce((rSum, qty) => rSum + qty, 0),
                                                                                    0
                                                                                ),
                                                                            0
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            </>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6">No data available</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                        <div className="pagination-controls" style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                                            <ButtonGroup aria-label="Basic example">
                                                <Button onClick={handleFirstPage} disabled={currentPage === 1}>First</Button>
                                                <Button onClick={handlePrevPage} disabled={currentPage === 1}>Prev</Button>

                                                {Array.from({ length: 3 }, (_, index) => {
                                                    let startPage = Math.max(1, Math.min(currentPage - 1, totalPages - 2)); 
                                                    let pageNumber = startPage + index;
                                                    
                                                    return (
                                                        pageNumber <= totalPages && (
                                                            <Button 
                                                                key={pageNumber} 
                                                                className={` ${currentPage === pageNumber ? 'active' : ''}`} 
                                                                onClick={() => handlePageChange(pageNumber)}
                                                                disabled={currentPage === pageNumber}
                                                            >
                                                                {pageNumber}
                                                            </Button>
                                                        )
                                                    );
                                                })}

                                                <Button onClick={handleNextPage} disabled={currentPage === totalPages}>Next</Button>
                                                <Button onClick={handleLastPage} disabled={currentPage === totalPages}>Last</Button>
                                            </ButtonGroup>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
            </Row>
        </Container>
    );
};
export default AvailableInventoryReports;